import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks";
import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {GQL_ACCOUNTUSER_UPDATE, GQL_USER_CONTEXT, UpdateAccountUserResult} from "@/lib/queries/accounts";
import {DateFormat, ProfileTimeFormatDisplay, ResponseStatus} from "@/lib/types/common";

interface UserProfileFormValues {
  email: string;
  first_name: string;
  last_name: string;
  current_date_format: string;
  current_time_format: string;
  notify_paid_invoices: boolean;
  notify_important_updates: boolean;
}

type UserProfileFormKeys = keyof UserProfileFormValues;

const requiredString = Yup.string().required("This field cannot be blank.");
const requiredBoolean = Yup.boolean().required("This field cannot be blank.");
const EditUserProfileSchema = Yup.object({
  email: Yup.string().email().required("This field cannot be blank."),
  first_name: requiredString,
  last_name: requiredString,
  current_date_format: requiredString,
  current_time_format: requiredString,
  notify_paid_invoices: requiredBoolean,
  notify_important_updates: requiredBoolean,
});

export const UserProfileFormDetails = () => {
  const {user} = useCurrentUserContext();

  const [updateAccountUser] = useMutation<UpdateAccountUserResult>(GQL_ACCOUNTUSER_UPDATE, {
    refetchQueries: [GQL_USER_CONTEXT],
  });

  const methods = useForm<UserProfileFormValues>({
    resolver: yupResolver<UserProfileFormValues>(EditUserProfileSchema),
    defaultValues: {
      email: user?.email,
      first_name: user?.first_name,
      last_name: user?.last_name,
      current_date_format: user?.current_date_format,
      current_time_format: user?.current_time_format,
      notify_paid_invoices: user?.notify_paid_invoices,
      notify_important_updates: user?.notify_important_updates,
    },
  });

  useEffect(() => {
    if (!methods.formState.isDirty) {
      methods.reset({
        email: user?.email,
        first_name: user?.first_name,
        last_name: user?.last_name,
        current_date_format: user?.current_date_format,
        current_time_format: user?.current_time_format,
        notify_paid_invoices: user?.notify_paid_invoices,
        notify_important_updates: user?.notify_important_updates,
      });
    }
  }, [user, methods]);

  const onSubmit = async (data: UserProfileFormValues) => {
    const resp = await updateAccountUser({
      variables: {
        data: {
          ...data,
          id: user?.id,
        },
      },
    });

    if (resp.data?.response?.status === ResponseStatus.SUCCESS) {
      toast.success("User profile updated successfully.");
    } else {
      if (resp.data?.response?.message) {
        Object.entries(resp.data.response.message).forEach(([fieldName, messageList]) => {
          methods.setError(fieldName as UserProfileFormKeys, {message: (messageList as string[])[0]});
        });
      }
    }
  };

  const dateFormatOptions = Object.entries(DateFormat).map(([value, label]) => ({
    label,
    value,
  }));

  const timeFormatOptions = Object.entries(ProfileTimeFormatDisplay).map(([value, label]) => ({
    label,
    value,
  }));

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          methods.handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="col-xl-6">
          <RequiredFieldsNote />
          <div className="row">
            <InputField name="email" label={"Email Address"} required={true} />
          </div>
          <div className="row mt-4">
            <div className="col-lg-6">
              <InputField name="first_name" label={"First Name"} required={true} />
            </div>
            <div className="col-lg-6">
              <InputField name="last_name" label={"Last Name"} required={true} />
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-lg-6">
              <SelectField
                name="current_date_format"
                label={"Date Format"}
                options={dateFormatOptions}
                isClearable={false}
              />
            </div>

            <div className="col-lg-6">
              <SelectField
                name="current_time_format"
                label={"Time Format"}
                options={timeFormatOptions}
                isClearable={false}
              />
            </div>
          </div>
          {user?.has_admin_access && (
            <div className="mt-4">
              <SwitchField className="mb-2" name="notify_paid_invoices" label="Send new invoices via email" />
            </div>
          )}
          <div className="mt-4">
            <SwitchField name="notify_important_updates" label="Notify me of important updates" />
          </div>

          {(window as Window & typeof globalThis & {Cookiebot?: {loaded: boolean}}).Cookiebot?.loaded && (
            <Button
              variant={"secondary"}
              className="mt-4"
              onClick={() =>
                document.getElementsByClassName("CookiebotWidget-logo")[0].dispatchEvent(new MouseEvent("click"))
              }
            >
              Cookie Settings
            </Button>
          )}
          <Divider className="mt-4" />
          <Button type="submit" className="mt-4">
            Save Changes
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
